import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, ArrowRight, DollarSign, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ContactPopup from '../components/ContactPopup';
import { processSteps } from '../mock';

const TrademarkRegistration = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const benefits = [
    'Nationwide exclusive rights to your brand name or logo',
    'Legal presumption of ownership and validity',
    'Ability to use the ® symbol',
    'Deterrent against potential infringers',
    'Public record of your ownership claim',
    'Basis for international trademark protection',
    'Increased brand value and credibility',
    'Legal grounds to sue for trademark infringement'
  ];

  const packages = [
    {
      name: 'Essentials',
      price: '$45',
      description: 'Great for small businesses covering multiple product or service classes.',
      note: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ],
      disclaimer: '(Govt. filing fee not included)'
    },
    {
      name: 'Enhanced',
      price: '$149',
      description: 'Great for small businesses covering multiple product or service classes.',
      note: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter'
      ],
      disclaimer: '(Govt. filing fee not included)'
    },
    {
      name: 'Deluxe',
      price: '$299',
      description: 'For brands needing extra support, including help with USPTO office actions.',
      note: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager'
      ],
      popular: true,
      recommended: true,
      disclaimer: '(Govt. filing fee not included)'
    },
    {
      name: 'Platinum',
      price: '$349',
      description: 'All-inclusive plan with monitoring, renewals, and full legal assistance.',
      note: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Trademark Search Report (Federal & State)',
        'High Priority Service',
        'Cease & Desist Letter'
      ],
      disclaimer: '(Govt. filing fee not included)'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Shield className="h-4 w-4 text-amber-500 mr-2" />
              <span className="text-sm text-amber-500 font-medium">Most Popular Service</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Trademark <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Registration</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Secure federal protection for your brand name, logo, or slogan. Our comprehensive trademark registration service ensures your intellectual property is protected across the United States.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Start Your Application
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Register Your Trademark?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Federal trademark registration provides powerful legal protection and business advantages.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your Package
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Transparent pricing with no hidden fees. All packages include USPTO filing fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg) => (
              <Card key={pkg.name} className={`bg-slate-900/60 border-white/20 relative ${pkg.popular ? 'border-white/60 shadow-xl shadow-white/10' : 'hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02]'} transition-all duration-300 flex flex-col h-[600px]`}>
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4 flex-shrink-0">
                  <CardTitle className="text-white text-xl mb-2">{pkg.name}</CardTitle>
                  <div className="text-3xl font-bold text-amber-500 mb-1">{pkg.price}</div>
                  <div className="text-xs text-gray-500 mb-2">{pkg.note}</div>
                  <CardDescription className="text-gray-400 text-sm leading-relaxed">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow">
                  <div className="flex-grow overflow-y-auto mb-4 pr-2" style={{ maxHeight: '250px' }}>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-300 text-xs">
                          <CheckCircle className="h-3 w-3 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <p className="text-xs text-gray-500 text-center mb-4">{pkg.disclaimer}</p>
                    <Button 
                      onClick={() => setIsPopupOpen(true)}
                      className={`w-full ${pkg.popular ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' : 'bg-slate-800 hover:bg-slate-700'} text-white font-semibold`}
                    >
                      Register My Trademark
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process - Matching Homepage Style */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Simple Process
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              From consultation to registration, we guide you through every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-white/20 rounded-xl p-8 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 hover:scale-[1.02] transition-all group">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl mb-6 shadow-xl group-hover:scale-110 transition-transform">
                    {step.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-500 h-8 w-8 z-10" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What's Included
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">No Hidden Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  All USPTO filing fees are included in our pricing. What you see is what you pay.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Fast Filing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  We file most applications within 24-48 hours of receiving your information.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border-white/20 text-center">
              <CardHeader>
                <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-amber-500" />
                </div>
                <CardTitle className="text-white">Expert Review</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-400 text-sm">
                  Every application is reviewed by our experienced trademark attorneys.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison: DIY vs Professional */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Why Choose Professional Trademark Registration?
            </h2>
            <p className="text-xl text-gray-400">
              See the difference expert guidance makes
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* DIY Filing */}
            <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <div className="text-center mb-6">
                <div className="bg-red-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-red-400 text-3xl">⚠️</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">DIY Filing</h3>
                <p className="text-red-400 font-semibold">60% Rejection Rate</p>
              </div>
              <ul className="space-y-3">
                {[
                  'Risk of incorrect classification',
                  'No comprehensive trademark search',
                  'Potential for office action rejections',
                  'No expert guidance on complex issues',
                  'Time-consuming research required',
                  'Risk of losing filing fees',
                  'No monitoring or renewal reminders'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-gray-400">
                    <span className="text-red-400 mr-3 mt-1">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Professional Service */}
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 backdrop-blur-sm border-2 border-amber-500/40 rounded-2xl p-8 shadow-xl shadow-amber-500/10">
              <div className="text-center mb-6">
                <div className="bg-amber-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-amber-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Our Service</h3>
                <p className="text-amber-400 font-semibold">98% Approval Rate</p>
              </div>
              <ul className="space-y-3">
                {[
                  'Comprehensive trademark search included',
                  'Expert classification and description',
                  'Attorney-prepared application',
                  'Office action response included',
                  'Fast 24-48 hour filing',
                  'Money-back guarantee',
                  'Lifetime monitoring and support'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start text-gray-200">
                    <CheckCircle className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs - Matching Homepage Style */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Frequently Asked
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Common questions about trademark registration
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {[
              {
                q: "How long does the trademark registration process take?",
                a: "The entire process typically takes 8-12 months from filing to registration. We file your application within 24-48 hours, and you'll receive a USPTO serial number immediately. The review process by the USPTO takes several months."
              },
              {
                q: "What can I trademark?",
                a: "You can trademark brand names, logos, slogans, product names, service names, and even sounds or colors in specific contexts. The mark must be distinctive and used in commerce."
              },
              {
                q: "What's included in your trademark search?",
                a: "Our comprehensive search includes federal and state trademark databases, common law usage, domain names, and social media handles to identify potential conflicts before filing."
              },
              {
                q: "What if my trademark application is rejected?",
                a: "We include one office action response in our service. Our experienced attorneys will work to overcome any objections from the USPTO. We also offer a money-back guarantee if we cannot proceed with your application."
              },
              {
                q: "Do I need a trademark for my business name?",
                a: "While not legally required, a trademark provides crucial legal protection. It prevents others from using similar names in your industry and gives you nationwide exclusive rights to your brand."
              },
              {
                q: "How much are the government fees?",
                a: "USPTO filing fees are $250-$350 per class of goods/services. Most businesses need 1-2 classes. These fees are separate from our service fees and are paid directly to the USPTO."
              }
            ].map((faq, index) => (
              <Accordion key={index} type="single" collapsible>
                <AccordionItem value={`item-${index}`} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-2 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all group">
                  <AccordionTrigger className="text-white font-bold text-left hover:text-amber-400 py-6 group-hover:scale-[1.01] transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-amber-400 font-black text-lg">{index + 1}</span>
                      </div>
                      <span className="flex-1">{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pl-14 pr-4 pb-6 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>

          {/* CTA below FAQ */}
          <div className="mt-12 text-center">
            <p className="text-gray-300 text-lg mb-6">Still have questions? We're here to help!</p>
            <Button 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-8"
            >
              Contact Our Experts
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
              Related Services
            </h2>
            <p className="text-xl text-gray-400">
              Complete protection for your intellectual property
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Trademark Search",
                desc: "Comprehensive search before filing to identify potential conflicts",
                link: "/services/trademark-search",
                icon: "🔍"
              },
              {
                title: "Copyright Registration",
                desc: "Protect your original works, content, and creative assets",
                link: "/services/copyright-registration",
                icon: "📝"
              },
              {
                title: "Amazon Brand Registry",
                desc: "Enroll your trademark in Amazon's Brand Registry program",
                link: "/services/amazon-brand-registry",
                icon: "🛍️"
              }
            ].map((service, idx) => (
              <Link key={idx} to={service.link}>
                <Card className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all h-full group cursor-pointer">
                  <CardHeader>
                    <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">{service.icon}</div>
                    <CardTitle className="text-white text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-gray-400">{service.desc}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-amber-400 font-semibold group-hover:translate-x-2 transition-transform">
                      Learn More <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-amber-500/20 via-slate-950 to-slate-950">
        <div className="absolute inset-0 backdrop-blur-sm" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border-2 border-white/60 rounded-3xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
              Ready to Protect Your Trademark?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join 5000+ businesses who have successfully registered their trademarks with our expert guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-10"
              >
                Register My Trademark Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>98% Approval Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>24-48hr Filing</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Money Back Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default TrademarkRegistration;
